import React from 'react';
import { Stack, Typography } from '@mui/material';
import GroupChannel from '@sendbird/uikit-react/GroupChannel';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import Message from '../Message';
import ChatHeader from './ChatHeader';
import EmptyChatIcon from './EmptyChatIcon';
import EmptyPanel from './EmptyPanel';
import UserProfileCard from './UserProfileCard';
import { EmptyChatIconBadge } from './StyledComponents';
import { targetMessageIdSelector, targetTimestampSelector } from 'domain/chat';

interface Props {
  channelUrl: string;
  onToggleSettings: (v: boolean) => void;
  onClose: () => void;
  onBackToList?: () => void;
  /** Mobile view — disables new-tab navigations in the chat header. */
  isMobile?: boolean;
}

const ChatPanel: React.FC<Props> = ({ channelUrl, onToggleSettings, onClose, onBackToList, isMobile = false }) => {
  const targetTimestamp = useSelector(targetTimestampSelector);
  const targetMessageId = useSelector(targetMessageIdSelector);
  const { formatMessage } = useIntl();

  const renderPlaceholderEmpty = () => (
    <Stack flex={1} alignItems="center" justifyContent="center" spacing={2.5}>
      <EmptyChatIconBadge>
        <EmptyChatIcon sx={{ fontSize: 64 }} />
      </EmptyChatIconBadge>
      <Typography variant="body1" color="text.secondary" textAlign="center">
        {formatMessage({ id: 'chat.panel.noMessages', defaultMessage: 'No messages' })}
      </Typography>
    </Stack>
  );

  return (
    <Stack flex={1} direction="column">
      {channelUrl ? (
        <GroupChannel
          key={channelUrl}
          channelUrl={channelUrl}
          startingPoint={targetTimestamp}
          animatedMessageId={targetMessageId}
          disableUserProfile={false}
          renderUserProfile={({ user, close }) => <UserProfileCard user={user} onClose={close} />}
          renderChannelHeader={() => (
            <ChatHeader
              onToggleSettings={onToggleSettings}
              onClose={onClose}
              onBackToList={onBackToList}
              isMobile={isMobile}
            />
          )}
          renderMessageContent={(props) => <Message {...props} />}
          renderPlaceholderEmpty={renderPlaceholderEmpty}
        />
      ) : (
        <EmptyPanel onClose={onClose} />
      )}
    </Stack>
  );
};

export default ChatPanel;
